import { CHILDREN_KEY, CONDITIONS, AUTO_COLUMN_KEY } from './metadata';
import { SpendTypeEnum } from '../types';
import isNumber from 'lodash/isNumber';
import orderBy from 'lodash/orderBy';
import moment from 'moment';

export const aggregateSumFuncByField = (field: string) => ({ data }: any) => {
  if (!data) {
    return;
  }

  if (data[CHILDREN_KEY]) {
    return data[CHILDREN_KEY].reduce((acc: number, item: any) => {
      const value = Number(item[field]) || 0;

      return acc + value;
    }, 0);
  }

  return data[field];
};

export const SPEND_LABELS = {
  [SpendTypeEnum.THIRD_PARTY]: 'Third party',
  [SpendTypeEnum.PERSONNEL]: 'Personnel',
};

export const convertCondition = (
  fieldName: string,
  { filter, filterTo, type, filterType, values, dateTo, dateFrom }: any,
  groupColumnKey: string
) => {
  const condition1Type = CONDITIONS[type];
  const isDateFilter = filterType === 'date';
  const key = fieldName === AUTO_COLUMN_KEY ? groupColumnKey : fieldName;
  let condition1Key = condition1Type ? `${key}_${condition1Type}` : key;
  let value = !isDateFilter
    ? type === 'string'
      ? filter.toString()
      : filter
    : dateFrom;

  if (filterType === 'set') {
    return {
      [`${key}_in`]: values,
    };
  }

  if (type === 'inRange') {
    const valueFrom = isDateFilter ? dateFrom : filter;
    const valueTo = isDateFilter ? dateTo : filterTo;

    return {
      AND: [
        { [`${key}_${CONDITIONS.greaterThan}`]: valueFrom },
        { [`${key}_${CONDITIONS.lessThan}`]: valueTo },
      ],
    };
  }

  return {
    [condition1Key]: value,
  };
};

export const generateFilters = (filterModel: any, groupColumnKey: string) => {
  return Object.keys(filterModel).map((key) => {
    const filterItem = filterModel[key];

    if (filterItem.operator) {
      return {
        [filterItem.operator]: [
          convertCondition(key, filterItem.condition1, groupColumnKey),
          convertCondition(key, filterItem.condition2, groupColumnKey),
        ],
      };
    } else {
      return convertCondition(key, filterItem, groupColumnKey);
    }
  });
};

export const getSortedNodes = (nodes: any[], sortModel: any) => {
  return nodes.map((node) => {
    if (!node[CHILDREN_KEY]) {
      return node;
    }

    const sortedChildren = sortModel.reduce(
      (acc: any, { sort, colId }: any) => {
        return orderBy(acc, colId, sort);
      },
      node[CHILDREN_KEY]
    );

    return {
      ...node,
      [CHILDREN_KEY]: sortedChildren,
    };
  });
};

export const getFilteredNodes = (nodes: any[], filterModel: any) => {
  return nodes.map((node) => {
    if (!node[CHILDREN_KEY]) {
      return node;
    }

    const filteredChildren = node[CHILDREN_KEY].filter((child: any) =>
      nodeFilter(child, filterModel)
    );

    return {
      ...node,
      [CHILDREN_KEY]: filteredChildren,
    };
  });
};

const nodeFilter = (node: any, filterModel: any) => {
  return Object.keys(filterModel).reduce((acc: boolean, filterKey: string) => {
    const filterItem = filterModel[filterKey];
    const { operator, filterType, condition1, condition2 } = filterItem;
    const comparator = getComparator(filterType);

    if (operator) {
      if (operator === 'AND') {
        return (
          acc &&
          comparator(condition1, node[filterKey]) &&
          comparator(condition2, node[filterKey])
        );
      }

      return (
        acc &&
        (comparator(condition1, node[filterKey]) ||
          comparator(condition2, node[filterKey]))
      );
    }

    return acc && comparator(filterItem, node[filterKey]);
  }, true);
};

const getComparator = (filterType: string) => {
  switch (filterType) {
    case 'text':
      return textComparator;
    case 'number':
      return numberComparator;
    case 'date':
      return dateComparator;
    default:
      return () => false;
  }
};

const textComparator = ({ type, filter }: any, value: any) => {
  const filterTextLowerCase = filter.toLowerCase();
  const valueLowerCase = value ? value.toString().toLowerCase() : '';

  switch (type) {
    case 'contains':
      return valueLowerCase.includes(filterTextLowerCase);
    case 'notContains':
      return !valueLowerCase.includes(filterTextLowerCase);
    case 'equals':
      return valueLowerCase === filterTextLowerCase;
    case 'notEqual':
      return valueLowerCase !== filterTextLowerCase;
    case 'startsWith':
      return valueLowerCase.startsWith(filterTextLowerCase);
    case 'endsWith':
      return valueLowerCase.endsWith(filterTextLowerCase);
    default:
      return false;
  }
};

const numberComparator = ({ type, filter, filterTo }: any, value: any) => {
  if (!isNumber(value)) {
    return false;
  }

  switch (type) {
    case 'equals':
      return value === filter;
    case 'notEqual':
      return value !== filter;
    case 'greaterThan':
      return value > filter;
    case 'greaterThanOrEqual':
      return value >= filter;
    case 'lessThan':
      return value < filter;
    case 'lessThanOrEqual':
      return value <= filter;
    case 'inRange':
      return value > filter && value < filterTo;
    default:
      return false;
  }
};

const dateComparator = ({ type, dateFrom, dateTo }: any, value: any) => {
  switch (type) {
    case 'equals':
      return moment(value).isSame(dateFrom, 'D');
    case 'notEqual':
      return !moment(value).isSame(dateFrom, 'D');
    case 'greaterThan':
      return moment(value).isAfter(dateFrom, 'D');
    case 'lessThan':
      return moment(value).isBefore(dateFrom, 'D');
    case 'inRange':
      return (
        moment(value).isBefore(dateTo, 'D') &&
        moment(value).isAfter(dateFrom, 'D')
      );
    default:
      return false;
  }
};
